import { createFileRoute, Link } from "@tanstack/react-router";
import { BrandLogo, type Brand } from "../components/BrandLogo";
import { ContactForm } from "../components/ContactForm";
import { CONTACT_EMAIL } from "../lib/contact";

export const Route = createFileRoute("/about")({
  component: About,
});

const PROFILES: { brand: Brand; lib: string; href: string; handle: string; label: string; stat: string }[] = [
  { brand: "leetcode", lib: "libleetcode.so", href: "https://leetcode.com/u/binaryspecter/", handle: "binaryspecter", label: "LeetCode", stat: "profile ↗" },
  { brand: "codechef", lib: "libcodechef.so", href: "https://www.codechef.com/users/rounak1974", handle: "rounak1974", label: "CodeChef", stat: "3★ · rank 270 (global)" },
  { brand: "codeforces", lib: "libcodeforces.so", href: "https://codeforces.com/profile/rounakkumarsingh", handle: "rounakkumarsingh", label: "Codeforces", stat: "profile ↗" },
  { brand: "atcoder", lib: "libatcoder.so", href: "https://atcoder.jp/users/rounakkumarsingh", handle: "rounakkumarsingh", label: "AtCoder", stat: "profile ↗" },
];

const ELSEWHERE: { brand: Brand; label: string; handle: string; href: string }[] = [
  { brand: "github", label: "GitHub", handle: "rounakkumarsingh", href: "https://github.com/rounakkumarsingh" },
  { brand: "linkedin", label: "LinkedIn", handle: "rounakkumarsingh19", href: "https://www.linkedin.com/in/rounakkumarsingh19/" },
];

// Narrative + linked-libraries (.lib-row) block, ported from
// design-reference/about.html.
function About() {
  return (
    <>
      <p className="frame-label">
        frame <b>#1</b> — about()
      </p>
      <div className="sec-head">
        <span className="addr">0x0001</span>
        <h2>who_am_i()</h2>
      </div>

      <p className="about-body">
        Currently compiling a <span className="kw">B.Tech</span> at IIEST Shibpur — CGPA 8.55/10.0 —
        with coursework spanning Object-Oriented System Design, Complexity Analysis, Algorithms,
        Operating Systems, and Formal Languages &amp; Automata Theory.
      </p>
      <br />
      <p className="about-body">
        Spent a summer interning at <span className="kw">Accenture</span>, redesigning a single-step
        LLM generation pipeline into a 3-stage multi-agent workflow orchestrated with LangGraph —
        deployed as fault-isolated async workers on Celery, RabbitMQ, and Redis.
      </p>
      <br />
      <p className="about-body">
        Outside of coursework I build things that sit close to the metal: a bytecode VM with its own
        garbage collector, a recursive DNS resolver, a BitTorrent client — the kind of software most
        people only ever import. Currently building <span className="kw">repolace</span>, an
        autonomous software-engineering agent platform that opens real pull requests against real
        issues, and <span className="kw">peerwire</span>, a BitTorrent client.
      </p>
      <br />
      <p className="about-body">When not writing code: competitive programming, and streaming chess on the side.</p>

      <div className="libs" style={{ maxWidth: "60ch", borderTop: "1px solid var(--rule)", marginTop: "1rem" }}>
        {PROFILES.map((p) => (
          <a
            key={p.brand}
            className="lib-row"
            href={p.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <BrandLogo brand={p.brand} />
            <span className="name">
              {p.lib}
              <span className="handle">
                {p.label} · {p.handle}
              </span>
            </span>
            <span className="status">resolved</span>
            <span className="stat">
              {p.stat}
              <span className="ext-note"> (opens in a new tab)</span>
            </span>
          </a>
        ))}
      </div>

      <section className="section-gap" aria-labelledby="elsewhere-h">
        <div className="sec-head">
          <span className="addr">0x0002</span>
          <h2 id="elsewhere-h">Elsewhere</h2>
        </div>
        <p className="about-body">Open to off-campus SDE roles and internships.</p>
        <div className="contact-line">
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </div>
        <div className="contact-line">
          {ELSEWHERE.map((l) => (
            <a key={l.brand} className="has-logo" href={l.href} target="_blank" rel="noopener noreferrer">
              <BrandLogo brand={l.brand} />
              <span className="label">{l.label}</span>
              <span className="handle">{l.handle}</span>
              <span className="ext-note"> (opens in a new tab)</span>
            </a>
          ))}
        </div>
      </section>

      <section id="contact" className="section-gap" aria-labelledby="contact-h">
        <div className="sec-head">
          <span className="addr">0x0003</span>
          <h2 id="contact-h">Contact</h2>
        </div>
        <p className="about-body">Send a message. All fields are required.</p>
        <ContactForm />
      </section>

      <Link to="/projects" className="cta primary">
        step into projects()
      </Link>
    </>
  );
}
